import { App } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { FormFinishInfo } from 'rc-field-form/lib/FormContext';
import { useEffect } from 'react';

import { userActions } from '@store/user/user.slice';

import { useMutation } from '@hook/common/use-mutation.hook';
import { useQuery } from '@hook/common/use-query.hook';
import { useStoreDispatch, useStoreSelector } from '@hook/store/store.hooks';
import { useAuthorized } from '@hook/user/use-authorized.hook';
import { useLoadProfileQuery, useSaveProfileMutation } from '@hook/user/user.hooks';
import { isExist, isString } from '@util/data/is-exist';

export const useUserProfile = () => {
  const dispatch = useStoreDispatch();
  const { message } = App.useApp();
  const { isAuthorized } = useAuthorized();
  const profile = useStoreSelector((state) => state.user.data.profile);
  const isLoading = useStoreSelector((state) => state.user.isLoading);
  const error = useStoreSelector((state) => state.user.error);
  const [form] = useForm();

  const { action: updateProfile, state } = useMutation(useSaveProfileMutation(), userActions, userActions.setProfile);

  useQuery(
    useLoadProfileQuery(undefined, {
      skip: !isAuthorized,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),
    userActions,
    userActions.setProfile,
  );

  useEffect(
    () => void (state.isSuccess && message.success('User profile was successfully updated')),
    [state.isSuccess],
  );

  const onUpdate = (_: string, { values }: FormFinishInfo) =>
    updateProfile({
      name: values.name,
      lastname: values.lastname,
      ...(isString(values.company) && { company: values.company }),
    });

  useEffect(() => void (isExist(profile) && form.setFieldsValue(profile)), [profile]);
  useEffect(() => () => void dispatch(userActions.resetErrors()), []);

  return {
    form,
    error,
    isLoading,
    onUpdate,
  };
};
